import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, ArrowDownLeft, ArrowUpRight, LogIn, Ban, ShieldCheck, ShieldOff } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "sso_login", label: "SSO Logins" },
  { id: "ban", label: "Bans" },
  { id: "unban", label: "Unbans" },
];

const KIND_META = {
  sso_login: { label: "SSO LOGIN", icon: LogIn, cls: "text-emerald-400 border-emerald-400/40" },
  sso_login_blocked: { label: "SSO BLOCKED", icon: ShieldOff, cls: "text-[var(--ff-red)] border-[var(--ff-red)]/40" },
  sso_issue: { label: "SSO ISSUED", icon: ArrowUpRight, cls: "text-sky-400 border-sky-400/40" },
  ban: { label: "BAN", icon: Ban, cls: "text-[var(--ff-red)] border-[var(--ff-red)]/40" },
  unban: { label: "UNBAN", icon: ShieldCheck, cls: "text-emerald-400 border-emerald-400/40" },
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

export default function BridgeAudit() {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async (kind = filter) => {
    setBusy(true);
    try {
      const q = kind && kind !== "all" ? `?kind=${kind}&limit=200` : "?limit=200";
      const { data } = await api.get(`/admin/bridge/events${q}`);
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setEvents([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="bridge-audit-tab">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2">▮ COMMUNITY LOUNGE / BRIDGE</div>
          <h2 className="font-display text-3xl">Cross-App Audit Log</h2>
          <p className="text-[var(--ff-text-dim)] text-sm mt-2 font-mono-ui max-w-2xl">
            Every single sign-on and every ban/unban that crossed the bridge between Freshy Fresh and Community Lounge. Inbound = came from the other app; outbound = sent by us.
          </p>
        </div>
        <button
          onClick={() => load(filter)}
          disabled={busy}
          className="btn-ghost self-start md:self-auto"
          data-testid="bridge-audit-refresh"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-px bg-[var(--ff-border)] mb-6" data-testid="bridge-audit-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`bridge-filter-${f.id}`}
            className={`px-4 py-2.5 font-mono-ui text-[0.7rem] uppercase tracking-[0.18em] transition-colors ${
              filter === f.id ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-surface)] text-[var(--ff-text-dim)] hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {events === null ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm p-8">Loading…</div>
      ) : events.length === 0 ? (
        <div className="surface-card p-10 text-center" data-testid="bridge-audit-empty">
          <p className="font-mono-ui text-sm text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
            No bridge activity yet.
          </p>
          <p className="text-xs text-[var(--ff-text-dim)] mt-2">
            Events appear here once users sign in via Community Lounge or a ban is synced across apps.
          </p>
        </div>
      ) : (
        <div className="surface-card overflow-x-auto" data-testid="bridge-audit-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
                <th className="p-3">When</th>
                <th className="p-3">Event</th>
                <th className="p-3">Dir</th>
                <th className="p-3">User</th>
                <th className="p-3">By</th>
                <th className="p-3">Reason / Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => {
                const meta = KIND_META[ev.kind] || { label: (ev.kind || "").toUpperCase(), icon: LogIn, cls: "text-[var(--ff-text-dim)] border-[var(--ff-border)]" };
                const Icon = meta.icon;
                const inbound = ev.direction === "inbound";
                return (
                  <tr key={ev.id} className="border-t border-[var(--ff-border)] align-top" data-testid={`bridge-event-${ev.id}`}>
                    <td className="p-3 whitespace-nowrap text-[var(--ff-text-dim)] font-mono-ui text-xs">{fmt(ev.at)}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] ${meta.cls}`}>
                        <Icon size={12} /> {meta.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] ${inbound ? "text-emerald-400" : "text-sky-400"}`}>
                        {inbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                        {inbound ? "IN" : "OUT"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="text-white">{ev.username || "—"}</div>
                      <div className="text-[var(--ff-text-dim)] font-mono-ui text-[0.65rem]">{ev.discord_id || ""}</div>
                    </td>
                    <td className="p-3 text-[var(--ff-text-dim)] font-mono-ui text-xs">{ev.actor || (ev.kind?.startsWith("sso") ? "community_lounge" : "—")}</td>
                    <td className="p-3 text-[var(--ff-text-dim)] text-xs max-w-[280px]">
                      {ev.reason || ev.detail || "—"}
                      {ev.delivered === false && <span className="ml-2 text-[var(--ff-red)] font-mono-ui text-[0.6rem] uppercase">not delivered</span>}
                      {ev.delivered === true && <span className="ml-2 text-emerald-400 font-mono-ui text-[0.6rem] uppercase">delivered</span>}
                      {ev.matched_user === false && <span className="ml-2 text-[var(--ff-text-dim)] font-mono-ui text-[0.6rem] uppercase">no local user</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
